import { Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, FormGroup, Grid, TextField, Typography, useTheme } from '@mui/material';
import { FormEvent, SyntheticEvent, useContext, useEffect, useState } from 'react';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { StockCountSummaryReportRequest, Store } from '../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { formatDateForReport, formatDateTimeForDisplay, filterStoreOptions } from '../../../utils';
import { UserContext } from '../../../components/shared/useUser';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import { StoreScope } from '../components/StoreScopePicker';
import AdvancedFilters from '../components/AdvancedFilters';
import dayjs from 'dayjs';
import useShiftConfiguration from '../../../hooks/configurations/useShiftConfiguration';

type IProps = {
  /** Fired after a report is successfully queued so the page can refresh status panels. */
  onGenerated?: () => void;
  onGenerationFailed?: (message?: string) => void;
  storeScope?: StoreScope;
  /** Full scope pickers rendered inside the merged Advanced filters collapse. */
  advancedScopeContent?: React.ReactNode;
}

const StockCountSummaryReportForm = (props: IProps) => {
  const { user } = useContext(UserContext);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  // Default to yesterday — the report reads completed-day data, and an empty date sends a bad request.
  const [date, setDate] = useState<string>(dayjs().subtract(1, 'day').format('YYYY-MM-DD'));
  const [errorMessage, setErrorMessage] = useState<string>('');
  // Flipped on failed submit validation so required fields render their error state.
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);
  const { runStockCountSummaryReport } = useNswagClient();
  const { t } = useTranslation('common');
  const theme = useTheme();

  const { calculateOperationalReportsRange } = useShiftConfiguration();
  const [operationalStart, setOperationalStart] = useState<Date | null>(null);
  const [operationalEnd, setOperationalEnd] = useState<Date | null>(null);
  const [isShiftEnabled, setIsShiftEnabled] = useState(true);

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectedStores([]);
    setCheckedAllStores(checked);
  };

  useEffect(() => {
    if (!date) return;

    const selectedAllStores =
      checkedAllStores || (selectedStores.length > 0 && selectedStores.length === user?.stores?.length);

    const store = selectedAllStores
      ? user?.stores?.[0]
      : selectedStores?.[0];

    if (!store?.storeNumber) return;

    const systemFranchises = user?.franchises?.filter(f =>
      user?.stores?.some(s => s.franchiseId === f.franchiseId),
    ) || [];

    const matchingFranchise = systemFranchises.find(f =>
      f.franchiseId === store?.franchiseId);

    calculateOperationalReportsRange(
      new Date(date),
      new Date(date),
      store.storeNumber,
      matchingFranchise?.franchiseName ?? '',
    ).then(({ start, end, isShiftEnabled }) => {
      setIsShiftEnabled(isShiftEnabled);
      setOperationalStart(start);
      setOperationalEnd(end);
    });
  }, [date, selectedStores, checkedAllStores, user?.stores]);

  const getMaxDate = () => {
    const today = dayjs().format('YYYY-MM-DD');
    return today;
  };

  const getMinDate = () => {
    const minDate = new Date('1900-01-01').toISOString().split('T')[0];
    return minDate;
  };

  const handleDateChange = (value: string | null) => {
    setDate(value ?? '');
  };

  const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    // Date is required — submitting without one sends a bad request.
    if (!date) {
      setShowErrors(true);
      setErrorMessage(t('reportingV2.date-required'));
      return;
    }
    setIsGenerating(true);
    setErrorMessage('');

    const reportDate = dayjs(date).startOf('day').toDate();
    const selectedAllStores = checkedAllStores || (selectedStores.length > 0 && selectedStores.length == user?.stores?.length);
    const scopedByPicker = !!props.storeScope;

    const request: StockCountSummaryReportRequest = {
      reportOnAllAvailableStores: scopedByPicker ? props.storeScope!.storeNumbers.length === 0 : selectedAllStores,
      stores: scopedByPicker
        ? (props.storeScope!.storeNumbers.length > 0 ? props.storeScope!.storeNumbers : undefined)
        : (selectedAllStores ? undefined : selectedStores.map(x => x.storeNumber as string)),
      date: formatDateForReport(reportDate),
      tenantId: props.storeScope?.tenantId,
      franchiseId: props.storeScope?.franchiseId,
    };

    runStockCountSummaryReport(request).then(() => {
      setIsRequestSubmitted(true);
      props.onGenerated?.();
    }).catch((error) => {
      props.onGenerationFailed?.(typeof (error) === 'string' ? error : undefined);
      console.log(error);
    }).finally(() => {
      setIsGenerating(false);
      setIsRequestSubmitted(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}
      noValidate>
      {date && operationalStart && operationalEnd && isShiftEnabled && (
        <Box
          sx={{
            mt: 2,
            display: 'inline-block',
            px: 2,
            py: 2,
            mb: 5,
            border: `1px solid ${theme.palette.secondary[300]}`,
            borderRadius: '8px',
          }}
        >
          <Typography
          >
            {t('shiftingConfiguration.filterPeriodApplied', {
              operationalStart: formatDateTimeForDisplay(operationalStart),
              operationalEnd: formatDateTimeForDisplay(operationalEnd),
            })}
          </Typography>
        </Box>
      )}
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        {!props.storeScope && (
          <>
            <Grid item
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  multiple
                  limitTags={2}
                  options={user?.stores ?? []}
                  value={selectedStores}
                  onChange={handleSelectChange}
                  disabled={checkedAllStores}
                  getOptionKey={(option) => option.storeNumber ?? ''}
                  getOptionLabel={(option) => option.storeName ?? ''}
                  filterOptions={filterStoreOptions}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={t('reporting.select-stores')}
                      placeholder={t('reporting.select-stores')} />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item
              sm={6}
              xs={12}>
              <FormGroup style={{ textAlign: 'right' }}>
                <FormControlLabel
                  control={<Checkbox
                    checked={checkedAllStores}
                    onChange={handleSelectAllChange} />}
                  label={t('reporting.all-stores')}
                />
              </FormGroup>
            </Grid>
          </>
        )}
        {/* Quick presets stay visible: Today/Yesterday chips. The full date picker lives
            under Advanced filters. */}
        <Grid item
          xs={12}
          sx={{ '&&': { paddingTop: '30px' } }}>
          <Typography variant="textMD"
            sx={{ fontWeight: 600, mb: '6px', display: 'block' }}>
            {t('date')}
          </Typography>
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { label: t('reportingV2.date-presets.today'), value: dayjs().format('YYYY-MM-DD') },
              { label: t('reportingV2.date-presets.yesterday'), value: dayjs().subtract(1, 'day').format('YYYY-MM-DD') },
            ].map((preset) => {
              const isActive = date === preset.value;
              return (
                <Chip
                  key={preset.label}
                  label={preset.label}
                  clickable
                  onClick={() => setDate(preset.value)}
                  variant={isActive ? 'filled' : 'outlined'}
                  aria-pressed={isActive}
                  sx={{
                    fontWeight: isActive ? 600 : 400,
                    bgcolor: isActive ? theme.palette.primary.main : 'transparent',
                    color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
                    borderColor: isActive ? theme.palette.primary.main : theme.palette.custom.gray[300],
                    '&:hover': { bgcolor: isActive ? theme.palette.primary.dark : theme.palette.custom.gray[100] },
                  }}
                />
              );
            })}
          </Box>
        </Grid>
        <Grid item
          xs={12}>
          <AdvancedFilters>
            <Grid container
              spacing={10}>
              {props.advancedScopeContent && (
                <Grid item
                  xs={12}>
                  {props.advancedScopeContent}
                </Grid>
              )}
              <Grid item
                sm={6}
                xs={12}>
                <DatePickerLocalized
                  label={t('date')}
                  value={date || null}
                  useCalendar={false}
                  error={showErrors && !date}
                  onChange={handleDateChange}
                  shouldDisableDate={(d) => d < getMinDate() || d > getMaxDate()}
                  translations={{
                    clearButtonLabel: t('calendar.clearButtonLabel'),
                    todayButtonLabel: t('calendar.todayButtonLabel'),
                    cancelButtonLabel: t('calendar.cancelButtonLabel'),
                    okButtonLabel: t('calendar.okButtonLabel'),
                    datePickerToolbarTitle: t('date'),
                  }}
                />
              </Grid>
            </Grid>
          </AdvancedFilters>
        </Grid>
        {
          errorMessage &&
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            disabled={isGenerating || isRequestSubmitted || (props.storeScope !== undefined && props.storeScope.franchiseId == null)}
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default StockCountSummaryReportForm;